import React, { FunctionComponent } from "react";
import { ICardElementWithChildren } from "backend/model/model";
import TextElement from "reportengine/TextElement";
import Report from "reportengine/Report";
import Slide from "reportengine/Slide";
import { createReport, saveBlob } from "reportengine/createReport";
import reportContextToRequest from "./reportContextToRequest";
import ProgressModal from "design/components/progress/ProgressModal";
import flattenHierarchy from "backend/flattenHierarchy";
import KPISlide from "./slides/KPISlide";
import InitiativeSlide from "./slides/InitiativeSlide";
import useElement from "backend/hooks/elements/useElement";
import useElements from "backend/hooks/elements/useElements";
import useTopElements from "backend/hooks/model/useTopElements";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";

const ConfigurableReport = (
  props: React.PropsWithChildren<{
    items: ICardElementWithChildren[];
    filetype: string;
    onClose?: () => void;
  }>
) => {
  const { filetype } = props;
  const [status, setStatus] = React.useState(t("reportGen.Generating Report").toString());
  const [progress, setProgres] = React.useState(0);
  const [blob, setBlob] = React.useState<null | Blob>(null);

  const { data: topElements, isLoading: topElementsLoading } = useTopElements();
  const { data: items } = useElements(props.items.map((item) => item.id));
  const ctx = useAppContext();

  const kpis = React.useMemo(() => {
    return items ? items.filter((item) => item.typename === "KPI") : [];
  }, [items]);
  const initiatives = React.useMemo(() => {
    return items ? items.filter((item) => item.typename === "Initiative") : [];
  }, [items]);

  if (!items) {
    return null;
  }
  if (!topElements || topElementsLoading) return null;

  return (
    <div>
      <Report
        template="/templates/report_en.pptx"
        onNothingToRender={() => {
          setStatus(t("reportGen.Nothing to render"));
        }}
        onProgress={(value) => {
          setProgres(value);
          console.log("slide progress ", value);
        }}
        onReadyToRender={async (report) => {
          setProgres(95);
          const blob = await createReport(
            await reportContextToRequest(report, "PerformanceReport", filetype)
          );
          setBlob(blob);
          setProgres(100);
        }}
      >
        <Slide index={0}>
          <TextElement slideElement="subtitle">{t("configurableReport.Configurable Report")}</TextElement>
          <TextElement slideElement="title">{t("configurableReport.Performance Report")}</TextElement>
        </Slide>
        {kpis.map((kpi) => {
          const topElement = topElements.find((topElement) => topElement.id === kpi.top_element_id);
          return <KPISlide item={kpi} 
            translatedScorecardName={getTranslatedAttribute(topElement, "Name", ctx.language)}/>;
        })}
        {initiatives.map((initiative) => {
          const topElement = topElements.find((topElement) => topElement.id === initiative.top_element_id);
          return <InitiativeSlide item={initiative} 
            translatedScorecardName={getTranslatedAttribute(topElement, "Name", ctx.language)}/>;
        })}
      </Report>
      <ProgressModal
        isOpen
        onClose={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
        isComplete={!!blob}
        cancelClickHandler={props.onClose}
        completedClickHandler={() => {
          if (blob) {
            saveBlob(`CustomizedReport.${filetype}`, blob);
          }
        }}
        downloadText={t("reportGen.Download")}
        progress={progress}
        statusText={status}
      />
    </div>
  );
};

export default ConfigurableReport;
